import React, { PropsWithChildren, useContext, useCallback } from 'react';

import { Case } from '../../../../shared/api/hscan/case';
import { CaseListContext } from '../../../../shared/context/CaseListContext';
import {
  DownloadOptions,
  DownloadProgressEventHandler,
} from '../../../../shared/hooks/caseHook';
import { QueryOption } from '../../../../shared/hooks/react-query-type';
import {
  useShareToPatientCase,
  useShareToPatientDicomImage,
  useShareToPatientDownloadImage,
  useGetShareToPatientLinkUrl,
  useShareToPatientSubscribeDownloadProgress,
} from '../../../../shared/hooks/shareToPatientHook';
import { SessionIdContext } from '../Preview/SessionIdContext';

export const ShareToPatientCaseHookProvider = ({
  children,
}: PropsWithChildren) => {
  const sessionId = useContext(SessionIdContext);

  const useCase = useCallback(
    (options?: QueryOption<Case[]>) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useShareToPatientCase(sessionId, options);
    },
    [sessionId],
  );

  const useDicomImage = useCallback(
    (imageId: string) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useShareToPatientDicomImage(imageId, sessionId);
    },
    [sessionId],
  );

  const useDownloadImage = useCallback(
    (caseIds: string[], sseKey: string, options?: DownloadOptions) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useShareToPatientDownloadImage(
        caseIds,
        sseKey,
        sessionId,
        options,
      );
    },
    [sessionId],
  );

  const useGetLinkUrl = useCallback(() => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useGetShareToPatientLinkUrl(sessionId);
  }, [sessionId]);

  const useSubscribeDownloadProgress = useCallback(
    (
      sseKey: string,
      downloadProgressEventHandler: DownloadProgressEventHandler,
    ) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useShareToPatientSubscribeDownloadProgress(
        sseKey,
        downloadProgressEventHandler,
        sessionId,
      );
    },
    [sessionId],
  );

  return (
    <CaseListContext.Provider
      value={{
        useCase,
        useDicomImage,
        useDownloadImage,
        useGetLinkUrl,
        useSubscribeDownloadProgress,
      }}>
      {children}
    </CaseListContext.Provider>
  );
};
